import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { controlService } from "../../../Services/control.service";
import FencePreviewMap from "./FencePreviewMap";

const DEFAULT_WIDTH_METERS = 250;

export default function GenerateFenceModal({
  show,
  onHide,
  routeResult,
  origin,
  destination,
  onSuccess,
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [widthMeters, setWidthMeters] = useState(DEFAULT_WIDTH_METERS);
  const [saving, setSaving] = useState(false);
  const [computing, setComputing] = useState(false);
  const [error, setError] = useState("");

  // Geometria calculada — reaproveitada tanto no preview quanto no payload
  // de salvar, pra não recalcular o buffer duas vezes.
  const [polylinePath, setPolylinePath] = useState(null); // [{lat,lng}]
  const [bufferPath, setBufferPath] = useState(null);       // array de polígonos: [ [ [pts exterior], [pts furo]... ], ... ]
  const [rawArea, setRawArea] = useState(null);              // string p/ payload (formato *aspas*)
  const [rawAreaPath, setRawAreaPath] = useState(null);

  const debounceRef = useRef(null);
  const mainItem = routeResult?.items?.[0];
  const encodedPolyline = mainItem?.encodedPolyline;

  const computeBuffer = useCallback(async (polyline, meters) => {
    if (!polyline || !window.google?.maps?.geometry?.encoding) {
      setPolylinePath(null);
      setBufferPath(null);
      setRawArea(null);
      setRawAreaPath(null);
      return;
    }

    setComputing(true);
    await new Promise((resolve) => setTimeout(resolve, 0)); // deixa o spinner pintar antes do trabalho pesado
    try {
      const turf = await import("@turf/turf");

      const decodedPath = window.google.maps.geometry.encoding.decodePath(polyline);
      const coords = decodedPath.map((ll) => [ll.lng(), ll.lat()]);

      if (coords.length < 2) {
        setPolylinePath(null);
        setBufferPath(null);
        return;
      }

      const line = turf.cleanCoords(turf.lineString(coords));
      const simplified = turf.simplify(line, { tolerance: 0.001, highQuality: false });
      const widthKm = Math.max(Number(meters) || DEFAULT_WIDTH_METERS, 10) / 1000;
      const buffered = turf.buffer(simplified, widthKm, { units: "kilometers", steps: 8 });
      const geom = buffered.geometry;


      const polygons =
        geom.type === "Polygon" ? [geom.coordinates] : geom.coordinates;
      const polygonRings = polygons.map((rings) =>
        rings.map((ring) => ring.map(([lngV, latV]) => ({ lat: latV, lng: lngV })))
      );

      const pathForMap = coords.map(([lngV, latV]) => ({ lat: latV, lng: lngV }));

      setPolylinePath(pathForMap);
      setBufferPath(polygonRings);
      setRawArea(JSON.stringify(polygonRings).replace(/"/g, "*"));
      setRawAreaPath(JSON.stringify(pathForMap));
    } catch (bufferErr) {
      console.warn("GenerateFenceModal: erro ao gerar buffer:", bufferErr);
      setPolylinePath(null);
      setBufferPath(null);
      setRawArea(null);
      setRawAreaPath(null);
    } finally {
      setComputing(false);
    }
  }, []);

  // Recalcula com debounce sempre que a rota ou a largura mudam — evita
  // rodar o turf a cada tecla digitada no campo de largura.
  useEffect(() => {
    if (!show || !encodedPolyline) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      computeBuffer(encodedPolyline, widthMeters);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [show, encodedPolyline, widthMeters, computeBuffer]);

  const handleHide = () => {
    setName("");
    setError("");
    setWidthMeters(DEFAULT_WIDTH_METERS);
    setPolylinePath(null);
    setBufferPath(null);
    setRawArea(null);
    setRawAreaPath(null);
    onHide?.();
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError(t("ELETRONIC_FENCES.PLEASE_ENTER_FENCE_DESCRIPTION"));
      return;
    }
    setError("");
    setSaving(true);

    try {
      const payload = {
        markerTypeId: 3,
        markerDescription: name.trim(),
        latitude: origin?.latitude ?? mainItem?.origin?.latitude ?? 0,
        longitude: origin?.longitude ?? mainItem?.origin?.longitude ?? 0,
        address: origin?.location ?? mainItem?.origin?.address?.formattedAddress ?? "",
        destinationLatitude: destination?.latitude ?? mainItem?.destination?.latitude ?? 0,
        destinationLongitude: destination?.longitude ?? mainItem?.destination?.longitude ?? 0,
        destinationAddress:
          destination?.location ?? mainItem?.destination?.address?.formattedAddress ?? "",
        radius: Number(widthMeters) || DEFAULT_WIDTH_METERS,
        // Usa a geometria já calculada pro preview — não recalcula aqui.
        area: rawArea ?? "NA",
        areaPath: rawAreaPath,
      };

      const response = await controlService.createFences(payload);
      if (response) {
        onSuccess?.();
        handleHide();
      } else {
        setError(t("ELETRONIC_FENCES.ERROR_SAVE_FENCE"));
      }
    } catch (err) {
      console.error("GenerateFenceModal.handleSave:", err);
      setError(t("ELETRONIC_FENCES.ERROR_SAVE_FENCE"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 12 }}>
          {t("ROUTING.GENERATE_FENCE")}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="d-flex gap-3 flex-wrap">
          <Form.Group style={{ flex: 2, minWidth: 220 }}>
            <Form.Label style={{ fontSize: 10 }}>
              {t("ELETRONIC_FENCES.FENCE_NAME")}
            </Form.Label>
            <Form.Control
              type="text"
              size="sm"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("ELETRONIC_FENCES.AREA_DESCRIPTION_PLACEHOLDER")}
              maxLength={100}
              onKeyDown={(e) => e.key === "Enter" && handleSave()}
            />
          </Form.Group>

          <Form.Group style={{ flex: 1, minWidth: 140 }}>
            <Form.Label style={{ fontSize: 10 }}>
              Largura da cerca (m)
            </Form.Label>
            <Form.Control
              type="number"
              size="sm"
              min={10}
              step={10}
              value={widthMeters}
              onChange={(e) => setWidthMeters(e.target.value)}
            />
          </Form.Group>
        </div>

        {error && (
          <div className="text-danger mt-1" style={{ fontSize: 10 }}>
            {error}
          </div>
        )}

        <div style={{ marginTop: 12, height: 340, position: "relative", borderRadius: 8, overflow: "hidden" }}>
          <FencePreviewMap polylinePath={polylinePath} polygonPaths={bufferPath} />
          {computing && (
            <div style={{
              position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
              background: "rgba(0,0,0,0.25)",
            }}>
              <Spinner animation="border" size="sm" />
            </div>
          )}
          {!encodedPolyline && !computing && (
            <div style={{
              position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 10, color: "var(--text-secondary,#aaa)",
            }}>
              Roteirize antes de gerar a cerca para ver o preview.
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={handleHide} disabled={saving}>
          {t("BUTTONS.CANCEL")}
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          disabled={saving || !name.trim()}
        >
          {saving ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="me-1" />
              {t("ELETRONIC_FENCES.SAVING")}
            </>
          ) : (
            t("BUTTONS.SAVE")
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
